import * as analytics from "@/modules/analytics";
import * as chainSession from "@/modules/ChainSession";
import config from "@/modules/config";
import { valueNames } from "@/modules/Constants";
import { findShopWindowConfigByCluster } from "@/modules/methodStep";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import { CHAIN_REVEAL_SECONDS } from "@/stores/chain/config";
import { chainStore } from "@/stores/chain/store";
import { isDoingStuff } from "@/stores/experience/interaction";
import { experienceStore } from "@/stores/experience/store";

interface TimeSyncPayload {
  name: string;
  time: number;
  duration: number;
}

interface ValuePayload<T> {
  region: string;
  value: T;
}

/** A timeline marker as TimelineMarkers passes it. */
interface VideoMarker {
  id: unknown;
  offset?: unknown;
}

interface MarkerPayload {
  region: string;
  timelineName: string;
  marker: VideoMarker;
}

export function timeSync(name: string, time: number, duration: number) {
  return experienceStore.dispatch(
    { name: name, time: time, duration: duration },
    (state, data: TimeSyncPayload) => {
      // The step index follows the cluster that is playing, found in the
      // shop-window list by cluster name, not only while its tab is open: the
      // last-step Up Next reveal reads that index.
      const config = findShopWindowConfigByCluster(
        state.applicationData,
        data.name,
      );
      let ret = false;
      state.experienceData.nowTime = data.time;

      // Publish the whole seconds remaining for the chain reveal and countdown.
      // Notify only inside the reveal window, not every second of the video.
      if (data.duration && data.duration > 0) {
        const remaining = Math.max(0, Math.ceil(data.duration - data.time));
        const prev = state.applicationData.getValue(
          valueNames.VIDEO_REMAINING,
          null,
        );
        if (prev !== remaining) {
          state.applicationData.setValue(valueNames.VIDEO_REMAINING, remaining);
          // The reveal latches (CHAIN_REVEALED) so scrubbing back never hides
          // the chain; inside the window every second notifies so the
          // countdown runs.
          if (remaining <= CHAIN_REVEAL_SECONDS) {
            if (
              !state.applicationData.getValue(valueNames.CHAIN_REVEALED, false)
            ) {
              state.applicationData.setValue(valueNames.CHAIN_REVEALED, true);
            }
            ret = true;
          }
        }
      }

      if (config && !isDoingStuff()) {
        const timeline = state.experienceData.getTimeline(data.name, 0);
        if (timeline) {
          const count = timeline.length;

          if (count === 0) {
            return ret;
          }

          let timelineIndex = -1;
          for (let i = 0; i < count; i++) {
            if (data.time >= (timeline[i].offset as number)) {
              timelineIndex = i;
            }
          }

          const configIndex =
            timelineIndex + config.mainIndexStart(state.experienceData);
          if (configIndex !== config.index) {
            config.index = configIndex;
            ret = true;
          }
        }
      }
      return ret;
    },
  );
}

export function setVideoPause(region: string, value: boolean) {
  return experienceStore.dispatch(
    { region: region, value: value },
    (state, data: ValuePayload<boolean>) => {
      if (state.applicationData.pauseVideo() === data.value) {
        return false;
      }

      state.applicationData.setPauseVideo(data.value);
    },
  );
}

export function setVideoPosition(region: string, value: number) {
  return experienceStore.dispatch(
    { region: region, value: value },
    (state, data: ValuePayload<number>) => {
      if (state.applicationData.videoPosition() === data.value) {
        return false;
      }

      state.applicationData.setVideoPosition(data.value);
    },
  );
}

export function videoMarkerSelected(
  region: string,
  timelineName: string,
  marker: unknown,
) {
  return experienceStore.dispatch(
    {
      region: region,
      timelineName: timelineName,
      marker: marker as VideoMarker,
    },
    (state, data: MarkerPayload) => {
      state.applicationData.setShowShopWindow(true);
      state.template.setShowPanel(true);

      analytics.track("Marker Clicked", {
        pix_appregion: data.region,
        pix_offset: state.experienceData.nowTime,
        pix_id: data.marker.id,
        pix_marker_offset: data.marker.offset,
        pix_cluster: data.timelineName,
      });

      return false;
    },
  );
}

export function videoMarkerReached(
  region: string,
  timelineName: string,
  marker: unknown,
) {
  return experienceStore.dispatch(
    {
      region: region,
      timelineName: timelineName,
      marker: marker as VideoMarker,
    },
    (state, data: MarkerPayload) => {
      state.experienceData.videoMarkerReached(data.marker);
      if (
        state.experienceData.getVideoMarkerReachedCount() === 1 &&
        config.showShopWindowOnMarker()
      ) {
        state.applicationData.setShowShopWindow(true);
        state.template.setShowPanel(true);
      } else {
        return false;
      }
    },
  );
}

export function videoEnded(timelineName: string) {
  return experienceStore.dispatch(
    { timelineName: timelineName },
    (state, data: { timelineName: string }) => {
      if (state.experienceData) {
        chainSession.recordWatched(
          state.experienceData.idexperience,
          state.experienceData.title,
        );
      }
      state.applicationData.setValue(valueNames.VIDEO_ENDED, true);
      // timeupdate rounds up, so its last tick can read 1; pin 0 so the
      // countdown ring ends on 0.
      state.applicationData.setValue(valueNames.VIDEO_REMAINING, 0);
      // A short video can end before a timeupdate lands in the reveal window.
      state.applicationData.setValue(valueNames.CHAIN_REVEALED, true);
      // With a ready chain the next-video UI owns the end state; the reset to
      // slide 0 below is the no-chain fallback.
      if (
        chainStore.getStatus() === "ready" &&
        chainStore.getChain().length > 0
      ) {
        return;
      }
      const shopWindowConfig = state.applicationData.getValue(
        valueNames.SHOP_WINDOW_CONFIG,
      ) as ShopWindowConfig | undefined;
      if (
        shopWindowConfig &&
        shopWindowConfig.clusterName === data.timelineName &&
        !isDoingStuff() &&
        shopWindowConfig.hasOpeningSlide(state.experienceData)
      ) {
        shopWindowConfig.index = 0;
      } else {
        return false;
      }
    },
  );
}
